// Extraction mécanique de client.ts (WO-V3-02 / US-019) — méthodes API du domaine.
import { request } from './http'
import type { AgentExecutionRequest, AgentExecutionResult, AgentExecutionStatus, Model, RagAnswer } from './types/aiAdmin'
import type { PortfolioRow } from './types/approvals'
import type { PublishedArtifact, SourceDocument, TimelineEvent } from './types/collaboration'
import type { BackupStatus, Deployment, Environment, Release, RollbackCommand, RollbackPreview, WorkerStatus } from './types/deployment'
import type { DocumentSummary } from './types/scmAudit'
import type { Agent } from './types/testManagement'
import type { AxisItem, ConfigTemplate, ProcessPlan, Program, ProgramSprint, TaxonomyAxis, TemplateConfig, TemplateProcessStep } from './types/workspaceTemplate'

export const programsTemplatesAndArtifactsApi = {
  // --- Programmes (niveau AU-DESSUS du projet, Piste C) ---
  listPrograms: () => request<Program[]>('/programs'),
  createProgram: (name: string, description?: string, templateId?: string) =>
    request<Program>('/programs', {
      method: 'POST',
      body: JSON.stringify({
        name,
        ...(description ? { description } : {}),
        // Template de configuration choisi (F1b) ; omis si non renseigné (retombe sur le Default).
        ...(templateId ? { template_id: templateId } : {}),
      }),
    }),
  getProgram: (id: string) => request<Program>(`/programs/${encodeURIComponent(id)}`),
  patchProgram: (id: string, patch: { name?: string; description?: string; status?: string }) =>
    request<Program>(`/programs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  attachWorkspaceToProgram: (programId: string, workspaceId: string) =>
    request<{ program_id: string; workspace_id: string; attached: boolean }>(
      `/programs/${encodeURIComponent(programId)}/workspaces/${encodeURIComponent(workspaceId)}`,
      { method: 'POST' }),
  detachWorkspaceFromProgram: (programId: string, workspaceId: string) =>
    request<{ program_id: string; workspace_id: string; attached: boolean }>(
      `/programs/${encodeURIComponent(programId)}/workspaces/${encodeURIComponent(workspaceId)}`,
      { method: 'DELETE' }),
  programPortfolio: (id: string) =>
    request<PortfolioRow[]>(`/programs/${encodeURIComponent(id)}/portfolio`),
  programRoadmap: (id: string) =>
    request<ProgramSprint[]>(`/programs/${encodeURIComponent(id)}/roadmap`),
  /** Cascade IA : décompose une initiative de programme en epics dans un projet cible. */
  generateProgramEpics: (programId: string, workspaceId: string, title: string, description?: string) =>
    request<{ job_id: string; workspace_id: string; status: string }>(
      `/programs/${encodeURIComponent(programId)}/initiatives/generate-epics`,
      { method: 'POST',
        body: JSON.stringify({ workspace_id: workspaceId, title, ...(description ? { description } : {}) }) }),
  /** Voie livrable : initiative → cadrage soumis au circuit d'approbation. */
  frameProgramInitiative: (programId: string, workspaceId: string, title: string, description?: string) =>
    request<{
      program_id: string; workspace_id: string; initiative_id: string; document_id: string;
      filename: string; status: string; message?: string; next_step: string; next_actions: string[];
    }>(
      `/programs/${encodeURIComponent(programId)}/initiatives/frame`, {
        method: 'POST',
        body: JSON.stringify({ workspace_id: workspaceId, title, ...(description ? { description } : {}) }),
      }),

  // --- Templates de configuration (F1b) : le Default global + ceux de l'organisation ---
  listTemplates: () => request<ConfigTemplate[]>('/templates'),
  getTemplate: (id: string) => request<ConfigTemplate>(`/templates/${encodeURIComponent(id)}`),
  createTemplate: (name: string, description: string | undefined, config: TemplateConfig) =>
    request<ConfigTemplate>('/templates', {
      method: 'POST',
      body: JSON.stringify({ name, ...(description ? { description } : {}), config }),
    }),
  updateTemplate: (id: string, patch: { name?: string; description?: string; config?: TemplateConfig }) =>
    request<ConfigTemplate>(`/templates/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  publishTemplate: (id: string) =>
    request<ConfigTemplate>(`/templates/${encodeURIComponent(id)}/publish`, { method: 'POST' }),
  publicationReadiness: (id: string) =>
    request<{ template_id: string; ready: boolean; blocked: string[] }>(
      `/templates/${encodeURIComponent(id)}/publication-readiness`),
  deleteTemplate: (id: string) =>
    request<{ id: string; deleted: boolean }>(`/templates/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  getTemplateProcess: (id: string) =>
    request<TemplateProcessStep[]>(`/templates/${encodeURIComponent(id)}/process`),
  replaceTemplateProcess: (id: string, steps: TemplateProcessStep[], workspaceId?: string) =>
    request<TemplateProcessStep[]>(`/templates/${encodeURIComponent(id)}/process${workspaceId
      ? `?workspace_id=${encodeURIComponent(workspaceId)}` : ''}`, {
      method: 'PUT', body: JSON.stringify(steps),
    }),
  executeProcessAction: (body: { workspace_id: string; step_key: string; idempotency_key: string; input?: Record<string, unknown> }) =>
    request<{ action_id: string; job_id?: string; run_id?: number; status: string; idempotent_replay?: boolean }>(
      '/process-actions', { method: 'POST', body: JSON.stringify(body) }),
  getProcessAction: (actionId: string) =>
    request<{ id: string; workspace_id: string; step_key: string; status: string; result?: string; error?: string }>(
      `/process-actions/${encodeURIComponent(actionId)}`),
  getProcessPlan: (workspaceId: string) =>
    request<ProcessPlan>(`/process-actions/plan?workspace_id=${encodeURIComponent(workspaceId)}`),
  startProcessRun: (body: { workspace_id: string; idempotency_key: string }) =>
    request<{ id: string; status: string; current_step?: string; action_id?: string;
      resume_required?: boolean; blockers?: unknown[] }>(
      '/process-actions/runs', { method: 'POST', body: JSON.stringify(body) }),
  getLatestProcessRun: (workspaceId: string) =>
    request<{ id?: string; workspace_id: string; status: string; current_step?: string;
      current_step_index?: number; total_steps?: number; current_action_id?: string | null;
      resume_required?: boolean; error?: string | null }>(
      `/process-actions/runs?workspace_id=${encodeURIComponent(workspaceId)}`),
  resumeProcessRun: (runId: string) =>
    request<{ id: string; status: string; current_step?: string; action_id?: string }>(
      `/process-actions/runs/${encodeURIComponent(runId)}/resume`, { method: 'POST' }),
  approveProcessRun: (runId: string, reason: string) =>
    request<{ id: string; status: string; current_step?: string; resume_required?: boolean;
      steps_completed?: number }>(
      `/process-actions/runs/${encodeURIComponent(runId)}/approve`, {
        method: 'POST', body: JSON.stringify({ reason }),
      }),

  getWorkerStatus: () => request<WorkerStatus>('/worker/status'),

  // Axes de rattachement (C1). `listAxes` sans espace renvoie les axes partagés du locataire.
  listAxes: (workspaceId?: string, kind?: string) => {
    const q = new URLSearchParams()
    if (workspaceId) q.set('workspace_id', workspaceId)
    if (kind) q.set('kind', kind)
    const suffixe = q.toString()
    return request<TaxonomyAxis[]>(`/taxonomy/axes${suffixe ? `?${suffixe}` : ''}`)
  },
  createAxis: (body: { kind: string; name: string; parent_id?: number; workspace_id?: string; description?: string }) =>
    request<{ id: number; name: string; kind: string }>('/taxonomy/axes',
      { method: 'POST', body: JSON.stringify(body) }),
  listAxisItems: (axeId: number, workspaceId: string) =>
    request<AxisItem[]>(`/taxonomy/axes/${axeId}/items?workspace_id=${encodeURIComponent(workspaceId)}`),
  listItemAxes: (itemId: number, workspaceId: string) =>
    request<TaxonomyAxis[]>(`/taxonomy/items/${itemId}/axes?workspace_id=${encodeURIComponent(workspaceId)}`),
  attachAxis: (itemId: number, body: { workspace_id: string; axe_id: number }) =>
    request<{ item_id: number; axe_id: number }>(`/taxonomy/items/${itemId}/axes`,
      { method: 'POST', body: JSON.stringify(body) }),
  detachAxis: (itemId: number, axeId: number, workspaceId: string) =>
    request<{ item_id: number; axe_id: number }>(
      `/taxonomy/items/${itemId}/axes/${axeId}?workspace_id=${encodeURIComponent(workspaceId)}`,
      { method: 'DELETE' }),

  // Release & RunOps (C4). Le refus de promotion arrive en 409 avec son motif : il est
  // remonté tel quel à l'écran, un « une erreur est survenue » ne serait pas exploitable.
  listEnvironments: (workspaceId: string) =>
    request<Environment[]>(`/runops/environments?workspace_id=${encodeURIComponent(workspaceId)}`),
  listReleases: (workspaceId: string) =>
    request<Release[]>(`/runops/releases?workspace_id=${encodeURIComponent(workspaceId)}`),
  listDeployments: (workspaceId: string) =>
    request<Deployment[]>(`/runops/deployments?workspace_id=${encodeURIComponent(workspaceId)}`),
  getBackupStatus: (workspaceId: string) =>
    request<BackupStatus>(`/runops/backup-status?workspace_id=${encodeURIComponent(workspaceId)}`),
  createEnvironment: (body: { workspace_id: string; name: string; rank?: number; url?: string;
    target_type?: 'docker'; compose_file?: string; healthcheck_url?: string;
    image_policy?: 'digest-pinned' | 'tag-allowed'; auto_stop_minutes?: number; lifetime_type?: 'persistent' | 'ephemeral'; source_dir?: string;
    source_ref_pattern?: string; require_gitlab_pipeline?: boolean; technologies?: string[];
    postgres_password_secret_ref?: string; is_production?: boolean }) =>
    request<{ id: string; name: string }>('/runops/environments',
      { method: 'POST', body: JSON.stringify(body) }),
  patchEnvironment: (id: string, body: { workspace_id: string; name?: string; rank?: number; url?: string;
    compose_file?: string; healthcheck_url?: string; image_policy?: 'digest-pinned' | 'tag-allowed';
    auto_stop_minutes?: number; lifetime_type?: 'persistent' | 'ephemeral'; source_dir?: string; source_ref_pattern?: string;
    require_gitlab_pipeline?: boolean; technologies?: string[]; postgres_password_secret_ref?: string;
    is_production?: boolean }) =>
    request<{ id: string; updated: boolean }>(`/runops/environments/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(body) }),
  createRelease: (body: { workspace_id: string; version: string; commit_sha: string; source_ref?: string; notes?: string }) =>
    request<{ id: string; version: string }>('/runops/releases',
      { method: 'POST', body: JSON.stringify(body) }),
  createDeployment: (body: { workspace_id: string; environment_id: string; release_id: string; gitlab_pipeline_id?: number; idempotency_key?: string; operation_type?: 'deploy' | 'redeploy' }) =>
    request<{ id: string; status: string }>('/runops/deployments',
      { method: 'POST', body: JSON.stringify(body) }),
  publishCompose: (workspaceId: string, deploymentId: string, targetBranch: string) =>
    request<{ iid: number; state: string; source_branch?: string; target_branch?: string; branch?: string;
      web_url?: string; content_sha256: string; installer_published?: boolean; installer_sha256?: string;
      documentation_published?: boolean; readme_sha256?: string }>(
      `/runops/deployments/${encodeURIComponent(deploymentId)}/publish-compose`,
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId, target_branch: targetBranch }) }),
  triggerPublishedComposePipeline: (workspaceId: string, deploymentId: string) =>
    request<{ id: number; status: string; ref: string; sha?: string; web_url?: string; branch: string }>(
      `/runops/deployments/${encodeURIComponent(deploymentId)}/publish-compose/pipeline`,
      { method: 'POST', body: JSON.stringify({ workspace_id: workspaceId }) }),
  previewRollbackDeployment: (workspaceId: string, deploymentId: string) =>
    request<RollbackPreview>(
      `/runops/deployments/${encodeURIComponent(deploymentId)}/rollback-preview?workspace_id=${encodeURIComponent(workspaceId)}`),
  rollbackDeployment: (workspaceId: string, deploymentId: string, rollbackPlanToken: string) =>
    request<RollbackCommand>(
      `/runops/deployments/${encodeURIComponent(deploymentId)}/rollback?workspace_id=${encodeURIComponent(workspaceId)}`,
      { method: 'POST', body: JSON.stringify({ rollback_plan_token: rollbackPlanToken }) }),
  /** FND-V050-050 (LIV-031) : issue OBSERVÉE d'un déploiement hors pipeline, tracée « manuelle ». */
  declareDeploymentOutcome: (deploymentId: string, status: 'succeeded' | 'failed' | 'rolled_back') =>
    request<{ id: string; status: string; outcome_source: string }>(
      `/runops/deployments/${encodeURIComponent(deploymentId)}/outcome`,
      { method: 'POST', body: JSON.stringify({ status }) }),
  autoRollbackDeployment: (deploymentId: string, body: { workspace_id: string; slo_key: string; observed_value: number; threshold: number; reason: string }) =>
    request<{ incident_id: number; job_id: number; rollback_pending: boolean }>(
      `/runops/deployments/${encodeURIComponent(deploymentId)}/auto-rollback`,
      { method: 'POST', body: JSON.stringify(body) }),
  listAgents: (workspaceId?: string) => request<Agent[]>(
    workspaceId ? `/agents?workspace_id=${encodeURIComponent(workspaceId)}` : '/agents'),
  listAgentExecutionStatus: (workspaceId: string) => request<AgentExecutionStatus[]>(
    `/agents/status?workspace_id=${encodeURIComponent(workspaceId)}`),
  resumeAgent: (workspaceId: string, agentId: string) => request<AgentExecutionResult>(
    `/agents/status/${encodeURIComponent(agentId)}/resume?workspace_id=${encodeURIComponent(workspaceId)}`,
    { method: 'POST' }),
  listWorkspaceAgentAccess: (workspaceId: string) => request<Array<{ agent_id: string; enabled: boolean }>>(
    `/workspaces/${encodeURIComponent(workspaceId)}/agents`),
  setWorkspaceAgentAccess: (workspaceId: string, agentId: string, enabled: boolean) => request<{
    workspace_id: string; agent_id: string; enabled: boolean
  }>(`/workspaces/${encodeURIComponent(workspaceId)}/agents/${encodeURIComponent(agentId)}`, {
    method: 'PUT', body: JSON.stringify({ enabled }),
  }),
  listModels: () => request<Model[]>('/models'),
  listRuntimeModels: (workspaceId?: string) => request<Model[]>(
    workspaceId
      ? `/models?runtime=true&workspace_id=${encodeURIComponent(workspaceId)}`
      : '/models?runtime=true',
  ),

  executeAgent: (body: AgentExecutionRequest) =>
    request<AgentExecutionResult>('/agents/execute', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  /**
   * `model_id` est obligatoire côté passerelle (`@NotBlank` sur `options.model_id`) :
   * l'omettre produit un 400. L'appelant doit donc avoir choisi un modèle.
   */
  ragQuery: (workspaceId: string, question: string, modelId: string, documentId?: string) =>
    request<RagAnswer>('/rag/query', {
      method: 'POST',
      body: JSON.stringify({
        workspace_id: workspaceId,
        question,
        options: { model_id: modelId },
        // Vide = toute la base documentaire de l'espace, comme avant. Un document
        // choisi borne la recherche a ses seuls fragments (v030-doc-adressable).
        ...(documentId ? { filters: { document_id: documentId } } : {}),
      }),
    }),

  searchDocuments: (workspaceId: string, q: string) =>
    request<DocumentSummary[]>(
      `/documents/search?workspace_id=${encodeURIComponent(workspaceId)}` +
      `&q=${encodeURIComponent(q)}`,
    ),

  /** Historique des sources téléversées d'un espace, avec leur empreinte (checksum). */
  listDocuments: (workspaceId: string) =>
    request<SourceDocument[]>(
      `/documents?workspace_id=${encodeURIComponent(workspaceId)}`),

  /** Supprime un document et ses chunks depuis le Core IA réel. */
  deleteDocument: (workspaceId: string, documentId: string) =>
    request<{ document_id: string; status: string; message: string }>(
      `/documents/${encodeURIComponent(documentId)}?workspace_id=${encodeURIComponent(workspaceId)}`,
      { method: 'DELETE' }),

  /** Artefacts PUBLIÉS d'un espace : chemin, empreinte de sortie, commit (anti-amnésie). */
  listArtifacts: (workspaceId: string) =>
    request<PublishedArtifact[]>(
      `/artifacts?workspace_id=${encodeURIComponent(workspaceId)}`),

  /** Timeline unifiée de l'espace : tous les événements, du plus récent au plus ancien. */
  timeline: (workspaceId: string) =>
    request<TimelineEvent[]>(
      `/timeline?workspace_id=${encodeURIComponent(workspaceId)}`),

}
